import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { User } from '../interfaces/user';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private tokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(this.getToken());
  private userSubject: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(this.getUser());

  constructor(private http: HttpClient, private router: Router) {}

  login(email: string, password: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}auth/login`, { email, password }, { withCredentials: true })
      .pipe(
        tap((response: any) => {
          this.setToken(response.token);
          this.setSessionId(response.sessionId);
          this.setUser(response.user);
        })
      );
  }

  register(name: string, email: string, password: string, phone: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}auth/register`, { name, email, password, phone });
  }

  getProfile(): Observable<any> {
    return this.http.get(`${environment.apiUrl}auth/profile`);
  }

  verifyEmail(token: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}auth/verify-email`, { token });
  }

  resendVerification(email: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}auth/resend-verification`, { email });
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}auth/forgot-password`, { email });
  }

  resetPassword(token: string, password: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}auth/reset-password/${token}`, { password });
  }

  logout(): void {
    this.http.post(`${environment.apiUrl}auth/logout`, {}, { withCredentials: true }).subscribe({ error: () => {} });
    this.setToken(null);
    this.setSessionId(null);
    this.setUser(null);

    const currentRoute = this.router.url;
    const protectedRoutes = ['/checkout', '/reservations', '/profile'];
    const shouldRedirect = protectedRoutes.some(route => currentRoute.startsWith(route));
    if (shouldRedirect) {
      this.router.navigate(['/home']);
    }
  }

  refreshTokens(): Observable<any> {
    return this.http.post(`${environment.apiUrl}auth/refresh`, {}, { withCredentials: true }).pipe(
      tap((response: any) => {
        this.setToken(response.token);
        this.setSessionId(response.sessionId);
      })
    );
  }

  getSessions(): Observable<any> {
    return this.http.get(`${environment.apiUrl}auth/sessions`, { withCredentials: true });
  }

  revokeSession(sessionId: string): Observable<any> {
    return this.http.delete(`${environment.apiUrl}auth/sessions/${sessionId}`, { withCredentials: true });
  }

  revokeAllSessions(): Observable<any> {
    return this.http.delete(`${environment.apiUrl}auth/sessions`, { withCredentials: true });
  }

  // Social login — inicia el flujo OAuth
  initSocialLogin(provider: 'google' | 'facebook' | 'apple'): Observable<{ redirectUrl: string }> {
    return this.http.get<{ redirectUrl: string }>(`${environment.apiUrl}auth/social/${provider}/init`);
  }

  // Canjea el sessionId del social login por la cookie httpOnly del refresh token
  exchangeSocialToken(sessionId: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}auth/social/exchange-token`, { sessionId }, { withCredentials: true });
  }

  completeProfile(name: string, phone: string): Observable<{ token: string }> {
    return this.http.post<{ token: string }>(`${environment.apiUrl}auth/complete-profile`, { name, phone });
  }

  createPassword(password: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}auth/create-password`, { password });
  }

  unlinkProvider(provider: string): Observable<any> {
    return this.http.delete(`${environment.apiUrl}auth/social/${provider}`);
  }

  reauth(credentials: { email: string; password: string }): Observable<any> {
    return this.http.post(`${environment.apiUrl}auth/reauth`, credentials);
  }

  async setTokenAsync(token: string): Promise<void> {
    this.setToken(token);
  }

  async setUserAsync(user: any): Promise<void> {
    this.setUser(user);
  }

  // SESSION ID
  setSessionId(id: string | null): void {
    if (id) {
      localStorage.setItem('sessionId', id);
    } else {
      localStorage.removeItem('sessionId');
    }
  }

  getSessionId(): string | null {
    return localStorage.getItem('sessionId');
  }

  // USER
  setUser(user: User | null): void {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
    this.userSubject.next(user);
  }

  getUser(): User | null {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  getUserChanges() {
    return this.userSubject.asObservable();
  }

  // TOKEN
  setToken(token: string | null): void {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
    this.tokenSubject.next(token);
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getTokenChanges(): Observable<string | null> {
    return this.tokenSubject.asObservable();
  }

  getScopeFromToken(): 'full' | 'onboarding' | null {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const padded = parts[1] + '='.repeat((4 - (parts[1].length % 4)) % 4);
      const decoded = JSON.parse(atob(padded));
      return decoded.scope === 'onboarding' ? 'onboarding' : 'full';
    } catch {
      return null;
    }
  }

  isProfileIncomplete(): boolean {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      const parts = token.split('.');
      if (parts.length !== 3) return false;
      const padded = parts[1] + '='.repeat((4 - (parts[1].length % 4)) % 4);
      const decoded = JSON.parse(atob(padded));
      return decoded.profile_completed === false;
    } catch {
      return false;
    }
  }
}
